import { useAtomValue } from 'jotai';
import type { ViewStyle } from 'react-native';
import {
    BadgeLabel,
    BodyMediumRegular,
    BodySmallRegular,
    H6Emphasized,
    Picker,
    Spinner,
    SpinnerSize,
    useToastController,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { useGetAllAdminUsers } from '../../admin-users/hooks/useGetAllAdminUsers';
import { GroveSimpleModal } from '../../common/components/GroveSimpleModal';
import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../common/components/Toasts';
import { adminUserAtom } from '../../entities/entityAtoms';
import type { AdminUser } from '../../entities/entityTypes';
import {
    useListFruitSlicePermissions,
    useManageFruitSlicePermissions,
} from '../hooks/useFruitSlicePermissions';
import type { FruitSliceAccessLevel } from '../types/FruitSlicePermissionTypes';

/**
 * `NONE` represents no permission row (i.e. remove access). Access levels stack,
 * so the option order mirrors the backend hierarchy READ < WRITE < MODIFY.
 */
const NO_ACCESS_VALUE = 'NONE';
type AccessOptionValue = typeof NO_ACCESS_VALUE | FruitSliceAccessLevel;

const ACCESS_OPTIONS: Array<{ label: string; value: AccessOptionValue }> = [
    { label: 'No access', value: NO_ACCESS_VALUE },
    { label: 'Read', value: 'READ' },
    { label: 'Write', value: 'WRITE' },
    { label: 'Modify', value: 'MODIFY' },
];

interface FruitSlicePermissionsModalProps {
    sliceId: string;
    sliceName: string;
    ownerId: string | null;
    isOpen: boolean;
    onClose: () => void;
}

export const FruitSlicePermissionsModal = ({
    sliceId,
    sliceName,
    ownerId,
    isOpen,
    onClose,
}: FruitSlicePermissionsModalProps) => {
    const toast = useToastController();
    const currentAdminUser = useAtomValue(adminUserAtom);

    const { data: adminUsers = [], isPending: isLoadingAdminUsers } =
        useGetAllAdminUsers({ activeOnly: true });
    const { data: permissions = [], isPending: isLoadingPermissions } =
        useListFruitSlicePermissions(
            { id: sliceId },
            { enabled: isOpen && sliceId.length > 0 },
        );

    const { upsertPermissionAsync, deletePermissionAsync } =
        useManageFruitSlicePermissions(sliceId);

    const accessLevelByAdminUserId = new Map<string, FruitSliceAccessLevel>(
        permissions.map((permission) => [
            permission.adminUserId,
            permission.accessLevel,
        ]),
    );

    const shareableAdminUsers = adminUsers
        .filter((adminUser): adminUser is AdminUser & { id: string } =>
            Boolean(adminUser.id),
        )
        .sort((a, b) => a.email.localeCompare(b.email));

    const handleSelect = async (
        adminUser: AdminUser & { id: string },
        value: AccessOptionValue,
    ): Promise<void> => {
        const nextLevel = value === NO_ACCESS_VALUE ? null : value;
        const currentLevel = accessLevelByAdminUserId.get(adminUser.id) ?? null;

        if (nextLevel === currentLevel) {
            return;
        }

        try {
            if (nextLevel == null) {
                await deletePermissionAsync(adminUser.id);
                toast.show('Access removed', MutationSuccessToastOptions);
            } else {
                await upsertPermissionAsync(adminUser.email, nextLevel);
                toast.show('Access updated', MutationSuccessToastOptions);
            }
        } catch (error) {
            toast.show(
                'Error updating access',
                MutationErrorToastOptions(error),
            );
        }
    };

    const isLoading = isLoadingAdminUsers || isLoadingPermissions;

    return (
        <GroveSimpleModal
            isOpen={isOpen}
            onClose={onClose}
            hasCloseButton
            renderInAppRoot={false}
        >
            <YStack width={560} maxWidth="90vw" gap="$4" padding="$2">
                <YStack gap="$1">
                    <H6Emphasized>Manage access</H6Emphasized>
                    <BodySmallRegular color="$gray1000">
                        {sliceName}
                    </BodySmallRegular>
                </YStack>

                {isLoading ? (
                    <XStack justifyContent="center" padding="$6">
                        <Spinner size={SpinnerSize.SMALL} />
                    </XStack>
                ) : (
                    <YStack
                        gap="$3"
                        maxHeight="60vh"
                        overflow={'auto' as ViewStyle['overflow']}
                    >
                        {shareableAdminUsers.map((adminUser) => {
                            const isOwner = adminUser.id === ownerId;
                            const isCurrentUser =
                                adminUser.id === currentAdminUser.id;
                            const currentValue: AccessOptionValue =
                                accessLevelByAdminUserId.get(adminUser.id) ??
                                NO_ACCESS_VALUE;

                            return (
                                <XStack
                                    key={adminUser.id}
                                    alignItems="center"
                                    justifyContent="space-between"
                                    gap="$3"
                                >
                                    <YStack flex={1}>
                                        <BodyMediumRegular>
                                            {adminUser.email}
                                        </BodyMediumRegular>
                                    </YStack>
                                    {isOwner ? (
                                        <BadgeLabel
                                            variant="neutral"
                                            text="Owner"
                                            size="regular"
                                        />
                                    ) : isCurrentUser ? (
                                        <BadgeLabel
                                            variant="neutral"
                                            text="You"
                                            size="regular"
                                        />
                                    ) : (
                                        <View width={150}>
                                            <Picker<AccessOptionValue>
                                                value={currentValue}
                                                options={ACCESS_OPTIONS}
                                                size="tiny"
                                                onChange={(value) => {
                                                    void handleSelect(
                                                        adminUser,
                                                        value,
                                                    );
                                                }}
                                            />
                                        </View>
                                    )}
                                </XStack>
                            );
                        })}
                    </YStack>
                )}
            </YStack>
        </GroveSimpleModal>
    );
};
